import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sliders, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const MATTER_LABELS = {
  purchase: "Purchase",
  sale: "Sale",
  remortgage: "Remortgage",
  transfer: "Transfer of Equity",
};

/**
 * Firm-wide default onboarding step presets, per matter type.
 * A step toggled OFF here is skipped automatically the first time an
 * onboarding link is sent for a matter of that type (solicitors can still
 * tweak it per case afterwards).
 */
export default function OnboardingStepPresets() {
  const [open, setOpen] = useState(false);
  const [allSteps, setAllSteps] = useState([]);
  const [labels, setLabels] = useState({});
  const [matterTypes, setMatterTypes] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/onboarding/step-presets`).then(res => res.data);
      setAllSteps(r.all_steps || []);
      setLabels(r.step_labels || {});
      setMatterTypes(r.matter_types || []);
      setPresets(r.presets || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load presets");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // A step is "skipped" for a matter type when it's in that type's preset list.
  const isSkipped = (mt, step) => (presets[mt] || []).includes(step);

  const toggle = (mt, step) => {
    setPresets(prev => {
      const cur = new Set(prev[mt] || []);
      if (cur.has(step)) cur.delete(step); else cur.add(step);
      return { ...prev, [mt]: Array.from(cur) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/onboarding/step-presets`, { presets }).then(res => res.data);
      setPresets(r.presets || presets);
      toast.success("Onboarding defaults saved");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save presets");
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] text-[#100f5d] hover:underline flex items-center gap-1 font-medium"
        data-testid="open-step-presets">
        <Sliders className="w-3.5 h-3.5" /> Firm defaults
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="step-presets-dialog">
          <DialogHeader>
            <DialogTitle>Onboarding step defaults</DialogTitle>
            <DialogDescription>
              Turn off any step a matter type shouldn't include — e.g. a remortgage can skip the TA forms.
              These apply automatically the first time an onboarding link is sent; you can still change them per case.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
          ) : (
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
              {matterTypes.map(mt => (
                <div key={mt} className="rounded-lg border border-slate-200 p-3" data-testid={`preset-matter-${mt}`}>
                  <p className="text-sm font-semibold text-slate-800 mb-2">{MATTER_LABELS[mt] || mt}</p>
                  <div className="flex flex-wrap gap-2">
                    {allSteps.map(step => {
                      const skipped = isSkipped(mt, step);
                      return (
                        <button
                          key={step}
                          onClick={() => toggle(mt, step)}
                          className={`text-xs px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 transition-colors ${
                            skipped
                              ? "border-slate-200 bg-slate-100 text-slate-400 line-through"
                              : "border-[#100f5d]/30 bg-white text-slate-700 hover:border-[#100f5d]"
                          }`}
                          title={skipped ? "Skipped for this matter type — click to include" : "Included — click to skip"}
                          data-testid={`preset-${mt}-${step}`}>
                          {labels[step] || step}
                          {skipped && <X className="w-3 h-3" />}
                        </button>
                      );
                    })}
                  </div>
                  {(presets[mt] || []).length > 0 && (
                    <p className="text-[11px] text-slate-500 mt-2">
                      Skips: {(presets[mt] || []).map(s => labels[s] || s).join(", ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving || loading} data-testid="save-step-presets">
              {saving ? "Saving…" : "Save defaults"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
